import { useCallback, useMemo } from 'react'
import { NetworkStatus } from '@apollo/client'

import type { TEntityLanguages } from 'entities/languages'
import type { ProviderKeyWithAll } from 'entities/provider'
import type { TimeRange, TrendingSubjectProvider } from 'generated/graphql'
import { useTrendingTagsQuery } from 'hooks-queries/trending-tags'
import { languagesUtils, providersUtils } from 'utils'

import { DEFAULT_LIMIT, DEFAULT_PAGINATION_TAKE, DEFAULT_TIME_RANGE, GET_PAGINATED_TRENDING_TAGS_LOG } from './consts'
import type { TrendingTag } from './types'

interface UseTrendingTagsProps {
  selectedLanguage: TEntityLanguages
  selectedProvider: ProviderKeyWithAll
  timeRange?: TimeRange
  perPage?: number
  limit?: number
}

export function useTrendingTags({
  selectedLanguage,
  selectedProvider,
  timeRange = DEFAULT_TIME_RANGE,
  perPage = DEFAULT_PAGINATION_TAKE,
  limit = DEFAULT_LIMIT,
}: UseTrendingTagsProps) {
  const queryVariables = useMemo(
    () => ({
      filters: {
        language: languagesUtils.getTaxonomyLanguage(selectedLanguage),
        provider: providersUtils.getProviderByKey(selectedProvider) as unknown as TrendingSubjectProvider,
      },
      timeRange: timeRange,
      pagination: {
        take: perPage,
      },
    }),
    [perPage, selectedLanguage, selectedProvider, timeRange]
  )

  const {
    data: trendingTagsData,
    networkStatus,
    error,
    refetch,
    fetchMore,
  } = useTrendingTagsQuery({
    queryName: 'getPaginatedTrendingTags',
    queryVariables,
    errorLog: GET_PAGINATED_TRENDING_TAGS_LOG,
  })

  const trendingTagList = useMemo(() => {
    const listData = trendingTagsData?.listTrendingSubjectPaginated?.data || []

    return listData.map((trendingTag): TrendingTag => {
      const post = trendingTag?.territoryMostEngaged?.posts?.[0]
      return {
        id: trendingTag.territoryId,
        name: trendingTag.name,
        topics: trendingTag.topics || [],
        totalPosts: trendingTag.territoryPerformance?.totalPosts ?? 0,
        totalCreators: trendingTag.territoryPerformance?.totalCreators ?? 0,
        post,
        provider: trendingTag.provider,
        language: trendingTag.language,
      }
    })
  }, [trendingTagsData?.listTrendingSubjectPaginated?.data])

  const isLoading = [NetworkStatus.loading, NetworkStatus.refetch, NetworkStatus.setVariables].includes(networkStatus)
  const isFetchingMore = networkStatus === NetworkStatus.fetchMore

  const total = trendingTagsData?.listTrendingSubjectPaginated?.total ?? 0
  const canFetchMore = trendingTagList.length < limit && trendingTagList.length < total

  const fetchMoreTrendingTags = useCallback(
    async ({ take }: { take?: number } = {}) => {
      if (!fetchMore || !canFetchMore) return

      await fetchMore({
        variables: {
          pagination: {
            take: take ?? perPage,
            cursor: trendingTagsData?.listTrendingSubjectPaginated?.cursor,
          },
        },
      })
    },
    [fetchMore, canFetchMore, perPage, trendingTagsData?.listTrendingSubjectPaginated?.cursor]
  )

  const memoizedValues = useMemo(
    () => ({
      trendingTagList,
      isLoading,
      isFetchingMore,
      canFetchMore,
      error,
      refetch,
      fetchMoreTrendingTags,
    }),
    [trendingTagList, isLoading, isFetchingMore, canFetchMore, error, refetch, fetchMoreTrendingTags]
  )

  return memoizedValues
}
